import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";


import { api } from "../services/api";


import {
    InductionConfiguration
    , AcceptanceTermContextData
    , Induction
    , IFormInput,
    QuizInduction,
    QuestionsResponse,
    FieldsInduction,
    BeneficiaryPreCadastro,
} from "../TypesOrInterfaces/induction";

import jwtDecode from "jwt-decode";
import { getBeneficiaryPreCadastro } from "../services/beneficiaryPreCadastro";

interface AcceptanceTermProviderProps {
    children: ReactNode;
}

interface ResponseBeneficiary {
    data: Induction;
    success: boolean;
    error?: {
        code: number,
        message: string
    }
}

interface ResponseConfiguration {
    data: InductionConfiguration;
    success: boolean;
    error?: {
        code: number,
        message: string
    }
}

interface ResponseBenefit {
    data: {
        id: number;
        name: string;
    };
    success: boolean;
    error?: {
        code: number,
        message: string
    }
}

interface Token {
    exp: number;
    iat: number;
}


const AcceptanceTermContext = createContext<AcceptanceTermContextData>({} as AcceptanceTermContextData);


export function AcceptanceTermProvider({ children }: AcceptanceTermProviderProps) {

    const [token, setToken] = useState<string>();
    const [configuration, setConfiguration] = useState<InductionConfiguration>({} as InductionConfiguration);

    const [beneficiaryRegister, setBeneficiaryRegister] = useState<Induction>({} as Induction);

    const [acceptedTheTerm, setAcceptedTheTerm] = useState(false);
    const [benefitName, setBenefitName] = useState('');
    const [termAnswered, setTermAnswered] = useState(false);

    const [isActiveLoader, setIsActiveLoader] = useState(false);

    const [benefitId, setBenefitId] = useState(() => {
        const params = window.location.hash;

        if (params) {

            if (params.includes('#/AcceptanceTerm/')) {
                return Number(params.replace('#/AcceptanceTerm/', '').split('/')[0]);
            }
        }

        toast.error('Parametros não enviados!');
        return 0;
    });

    const [beneficiaryPreCadNotFound, setBeneficiaryPreCadNotFound] = useState(false);

    const [cardHolderNumber, setCardHolderNumber] = useState(() => {

        const params = window.location.hash;

        if (params) {

            if (params.includes('#/AcceptanceTerm/')) {
                return String(params.replace('#/AcceptanceTerm/', '').split('/')[1]);
            }
        }

        return '';
    });

    const [quizId, setQuizId] = useState(() => {

        const params = decodeURIComponent(window.location.hash);

        if (params) {

            if (params.includes('#/AcceptanceTerm/')) {
                return Number(params.replace('#/AcceptanceTerm/', '').split('/')[2].replace(/\D/g, ''));
            }
        }

        return 0;
    });

    useEffect(() => {
        const authentication = async () => {

            const tokenLocal = localStorage.getItem('@EpharmaInductionToken');

            //incluir validações se o token é valido se não gera outro
            if (tokenLocal) {

                const decoded = jwtDecode<Token>(tokenLocal);

                if (new Date(decoded.exp * 1000) > new Date()) {
                    setToken(tokenLocal);
                    return;
                }

                await getAuthentication();
            }

            await getAuthentication();
        }

        authentication();

    }, []);

    async function getAuthentication() {
        setIsActiveLoader(true);

        await api.get('/Authentication/api/v1/OAuth/Authenticate'
            , {
                headers: {
                    'client_id': window.configInduction.authClientId,
                    'username': window.configInduction.authUsername,
                    'password': window.configInduction.authPassword
                }
            }
        ).then(response => {
            if (response.status !== 200) {
                toast.error('Erro no portal, tente novamente mais tarde ou entre em contato na ePharma');
            }

            localStorage.setItem('@EpharmaInductionToken', response.data.data.token.accessToken);

            setToken(response.data.data.token.accessToken);

        }).catch((error) => {
            toast.error('Ocorreu um erro na aplicação por favor tente mais tarde!')
        }).finally(() => {
            setIsActiveLoader(false);
        });

    }

    // //Seta o token no header como default
    // api.defaults.headers.authorization = `Bearer ${token}`;

    useEffect(() => {
        setIsActiveLoader(true);

        if (token) {
            Promise.all([

                api.get<ResponseConfiguration>(`/Client/api/v1/Benefit/Beneficiary/Register/Configuration/${benefitId}`
                    , {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                ),
                api.get<ResponseBenefit>(`/Client/api/v1/Benefit/${benefitId}`
                    , {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                ),
                api.get<ResponseBeneficiary>(`/Beneficiary/api/v1/Beneficiary/Dynamic/${benefitId}/${cardHolderNumber}`
                    , {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
            ]).then((values) => {
                setIsActiveLoader(true);
                setConfiguration(values[0].data.data);
                setBenefitName(values[1]?.data?.data.name);
                setBeneficiaryRegister(values[2]?.data.data);
            }).catch((error) => {
                console.error(`Error na consulta das configurações: ${error}`)
            }).finally(() => {
                setIsActiveLoader(false);
            });
        }
    }, [benefitId, token, cardHolderNumber]);

    useEffect(() => {
        async function validIfThermAlreadyAccept() {
            if (cardHolderNumber && quizId && token) {
                const beneficiaryPreCad = await getBeneficiaryPreCad();

                if (beneficiaryPreCad === null) {
                    // precad nao encontrado (usuario declinou)
                    setBeneficiaryPreCadNotFound(true);
                    setTermAnswered(true);
                }

                if (beneficiaryPreCad?.isAceite) {
                    setAcceptedTheTerm(true);
                    setTermAnswered(true);
                }
            }
        }

        validIfThermAlreadyAccept();
    }, [cardHolderNumber, quizId, token]);

    async function updateQuizzes(data: IFormInput, quizId: number) {

        try {
            setIsActiveLoader(true);

            const questionsFilter = beneficiaryRegister.quiz?.filter(f => f.id === quizId)[0]?.questions.map(question => {

                if (data.hasOwnProperty(String(question.id))) {
                    return {} as QuestionsResponse
                }
                return question;

            }).filter(f => f.id);


            const newValueQuestions = configuration.quiz?.filter(c => c.id === quizId)[0]?.questions.map(questionConfig => {

                if (data.hasOwnProperty(String(questionConfig.id))) {
                    return {
                        id: questionConfig.id,
                        value: data[questionConfig.id]
                    } as QuestionsResponse
                }

                return {} as QuestionsResponse
            }).filter(f => f.id);

            const responseQuizBeneficiary = beneficiaryRegister.quiz?.filter(f => f.id === quizId)[0];

            const body = {
                quiz: [
                    {
                        id: responseQuizBeneficiary.id,
                        configurationId: responseQuizBeneficiary.configurationId,
                        skuId: responseQuizBeneficiary.skuId,
                        questions: [...questionsFilter, ...newValueQuestions]
                    } as QuizInduction
                ]
            }

            const response = await api.put<ResponseBeneficiary>(`Beneficiary/api/v1/Beneficiary/DynamicQuizzes/${benefitId}/${cardHolderNumber}`
                , body, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.status !== 200) {
                toast.error('Ocorreu um erro ao atualizar o aceite de termo');

                return;
            }            
        }
        catch {
            toast.error('Ocorreu um erro na aplicação por favor tente mais tarde!');
            return;
        }
        finally {
            resetAll();
        }
    }

    function resetAll() {
        setIsActiveLoader(false);
    }

    async function getBeneficiaryPreCad(): Promise<BeneficiaryPreCadastro | null | undefined> {
        const result = await getBeneficiaryPreCadastro(token || "", cardHolderNumber || "", quizId);

        if (result.status === 404 || result.errorCode === 404) {
            setBeneficiaryPreCadNotFound(true);
            return null;
        }

        if (result.status === 401 || result.errorCode === 401) {
            setBeneficiaryPreCadNotFound(false);
            toast.error(result.message || 'Não foi possível validar o termo de aceite. Faça uma nova tentativa.');
            return undefined;
        }

        if (!result.data) {
            setBeneficiaryPreCadNotFound(false);
            if (result.message) {
                toast.error(result.message);
            }

            return undefined;
        }

        setBeneficiaryPreCadNotFound(false);
        return result.data;
    }

    async function updateBeneficiaryPredCad(beneficiaryPreCad: BeneficiaryPreCadastro) {
        await api.put("/Beneficiary/api/v1/BeneficiaryPreCadastro", beneficiaryPreCad, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    async function acceptTermPreRegister() {
        const beneficiaryPredCad = await getBeneficiaryPreCad();

        if (beneficiaryPredCad) {
            beneficiaryPredCad.isAceite = true;
            beneficiaryPredCad.dataAceite = new Date().toUTCString();
            
            await updateBeneficiaryPredCad(beneficiaryPredCad);
        }
    }

    async function acceptTerm(data: IFormInput) {
        await acceptTermPreRegister();

        if (beneficiaryRegister) {
            await updateQuizzes(data, quizId);
        }

        setAcceptedTheTerm(true);
        setTermAnswered(true);
        resetAll();
    }

    async function declineTerm() {
        await api.delete("/Beneficiary/api/v1/BeneficiaryPreCadastro", {
            headers: {
                cpf: cardHolderNumber,
                isPln: benefitId,
                Authorization: `Bearer ${token}`
            }
        });

        setTermAnswered(true);
        resetAll();
    }

    return (
        <AcceptanceTermContext.Provider
            value={{
                benefitId
                , configuration
                , beneficiaryRegister
                , cardHolderNumber
                , updateQuizzes
                , benefitName
                , quizId
                , isActiveLoader
                , acceptedTheTerm
                , termAnswered
                , beneficiaryPreCadNotFound
                , acceptTerm
                , declineTerm
            }}>
            {children}
        </AcceptanceTermContext.Provider>
    )
}

export function useAcceptanceTerm(): AcceptanceTermContextData {
    const context = useContext(AcceptanceTermContext);

    return context;
}
